/**
 * A receipt split shared from the Pebla app, kept on the server: `/s/:token`.
 * Friends join with their name and MoMo number, tick what they had (sharing
 * items and quantities as they go) and pay their share. Every amount comes
 * from the server.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { downloadLinks } from '../constants/links';
import {
  firstName,
  formatMomoNumber,
  formatRwf,
  initials,
  isValidPayerName,
  normalizeMomoNumber,
  telHref,
} from '../splitReceipt/model';
import { SplitApi, SplitApiError, splitApi } from './api';
import { LiveConnect, LiveStatus, connectLive } from './live';
import {
  Guest,
  clearGuest,
  destinationDial,
  hostName,
  joinNames,
  lineUnits,
  liveNotice,
  loadGuest,
  othersOn,
  personDial,
  ownersOf,
  participantOf,
  picksLocked,
  quote,
  saveGuest,
  unitsOf,
  withClaimsOf,
  withUnits,
} from './model';
import { Claim, Debt, Item, Participant, Session } from './types';
import '../splitReceipt/splitReceipt.css';
import './splitSession.css';

type Load =
  | { state: 'loading' }
  | { state: 'gone'; title: string; message: string }
  | { state: 'ready'; session: Session };

/** Why this browser is no longer on the split. */
type Kicked = 'elsewhere' | 'removed';

const defaultApi = splitApi();

const isMobile = () => /android|iphone|ipad|ipod/i.test(navigator.userAgent);

/** Opens the phone's dialer with a MoMo code; on a computer the code is shown instead. */
function openDialer(dial: string) {
  if (isMobile()) window.location.href = telHref(dial);
}

export const SplitSessionPage: React.FC<{ api?: SplitApi; storage?: Storage; connect?: LiveConnect }> = ({
  api = defaultApi,
  storage,
  connect = connectLive,
}) => {
  const { token = '' } = useParams();
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const [guest, setGuest] = useState<Guest | null>(() => loadGuest(token, storage));
  const [welcome, setWelcome] = useState<string | null>(null);
  const [kicked, setKicked] = useState<Kicked | null>(null);
  const [live, setLive] = useState<LiveStatus>('connecting');
  const [notice, setNotice] = useState<string | null>(null);

  const show = useCallback((session: Session) => setLoad({ state: 'ready', session }), []);

  // The live room is joined once per link; who "me" is can change underneath it.
  const meId = useRef<string | null>(guest?.participantId ?? null);
  meId.current = guest?.participantId ?? null;
  const ready = load.state === 'ready';
  useEffect(() => {
    if (!ready) return undefined;
    return connect(token, {
      onStatus: setLive,
      onUpdate: (event) => {
        show(event.session);
        const said = meId.current ? liveNotice(event, meId.current) : null;
        if (said) setNotice(said);
      },
    });
  }, [ready, connect, token, show]);

  useEffect(() => {
    document.title = 'Split the bill · Pebla';
  }, []);

  useEffect(() => {
    let alive = true;
    api.session(token).then(
      (session) => alive && show(session),
      (error: SplitApiError) =>
        alive &&
        setLoad(
          error.offline
            ? { state: 'gone', title: 'You’re offline', message: 'Check your connection, then open the link again.' }
            : { state: 'gone', title: 'This link doesn’t work', message: 'Ask the person who shared it to send it again.' },
        ),
    );
    return () => {
      alive = false;
    };
  }, [api, token, show]);

  /** The token went stale: someone joined as this person elsewhere, or the sharer removed them. */
  const leave = useCallback(
    (why: Kicked) => {
      clearGuest(token, storage);
      setGuest(null);
      setKicked(why);
    },
    [token, storage],
  );

  const session = load.state === 'ready' ? load.session : null;
  const me = participantOf(session, guest?.participantId);

  // Removed people drop out of the public view.
  useEffect(() => {
    if (session && guest && !me) leave('removed');
  }, [session, guest, me, leave]);

  /** Handles a refusal that means this browser isn't that person any more. True if it did. */
  const handled = useCallback(
    (error: unknown) => {
      if (!(error instanceof SplitApiError)) return false;
      if (error.code === 'participant_token_invalid') leave('elsewhere');
      else if (error.code === 'participant_removed') leave('removed');
      else return false;
      return true;
    },
    [leave],
  );

  return (
    <div className="sr-page">
      <header className="sr-topbar">
        <div className="sr-brand">
          <img src="/images/icons/pe.svg" alt="" width={28} height={28} />
          <span>Pebla</span>
        </div>
      </header>
      {load.state === 'loading' && <p className="sr-muted sr-center">Opening the bill…</p>}
      {load.state === 'gone' && <Notice title={load.title} message={load.message} />}
      {session && session.status === 'expired' && (
        <Notice title="This split has expired" message="Ask the person who shared it for a new link." />
      )}
      {session && session.status !== 'expired' && (!guest || !me) && (
        <JoinStep
          session={session}
          kicked={kicked}
          onJoin={async (details) => {
            const joined = await api.join(token, details);
            const next = { participantId: joined.participant.id, participantToken: joined.participantToken };
            saveGuest(token, next, storage);
            setKicked(null);
            setWelcome(joined.reconnected ? `Welcome back, ${firstName(joined.participant.displayName)}` : null);
            setGuest(next);
            show(joined.session);
          }}
        />
      )}
      {session && session.status !== 'expired' && guest && me && (
        <Bill
          api={api}
          token={token}
          session={session}
          guest={guest}
          me={me}
          welcome={welcome}
          notice={notice}
          live={live}
          onSession={show}
          onRefused={handled}
          onNotYou={() => {
            clearGuest(token, storage);
            setGuest(null);
            setWelcome(null);
          }}
        />
      )}
    </div>
  );
};

const JoinStep: React.FC<{
  session: Session;
  kicked: Kicked | null;
  onJoin: (details: { displayName: string; phoneNumber: string }) => Promise<void>;
}> = ({ session, kicked, onJoin }) => {
  const [name, setName] = useState('');
  const [momo, setMomo] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [touched, setTouched] = useState({ name: false, momo: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const vendor = session.destination?.type === 'vendor';
  const place = session.receipt.merchantName?.trim() ?? '';
  const number = normalizeMomoNumber(momo);
  const nameOk = isValidPayerName(name);
  const ready = nameOk && number !== null && agreed && !busy;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ name: true, momo: true });
    if (!nameOk || number === null || !agreed || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onJoin({ displayName: name.trim().replace(/\s+/g, ' '), phoneNumber: number });
    } catch (err) {
      setError(
        err instanceof SplitApiError && err.code === 'host_number'
          ? 'That’s the number of the person who shared this bill. Use your own MoMo number.'
          : err instanceof Error
          ? err.message
          : 'Something went wrong. Try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate>
      <main className="sr-main">
        <section className="sr-host">
          <div className="sr-host-icon" aria-hidden>
            🧾
          </div>
          <div>
            <h1>{vendor ? 'Split the bill' : `${hostName(session)} shared a bill`}</h1>
            <p>{[place, `Total ${formatRwf(session.receipt.totalMinor)}`].filter(Boolean).join(' · ')}</p>
          </div>
        </section>

        {kicked && (
          <p className="sr-card sr-small" role="status">
            {kicked === 'elsewhere'
              ? 'You’ve continued on another browser. To pay from here, enter your details again.'
              : 'The person who shared this bill took you off it. Join again if that was a mistake.'}
          </p>
        )}

        <section>
          <h2 className="sr-label">YOUR DETAILS</h2>
          <div className="sr-card sr-form">
            <label className="sr-field">
              <span>Full name</span>
              <input
                type="text"
                autoComplete="name"
                autoCapitalize="words"
                placeholder="e.g. Aline Uwase"
                value={name}
                aria-invalid={touched.name && !nameOk}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, name: true }))}
              />
              {touched.name && !nameOk && <small className="sr-field-error">Enter your name</small>}
            </label>
            <label className="sr-field">
              <span>MTN MoMo number</span>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="0788 123 456"
                value={momo}
                aria-invalid={touched.momo && number === null}
                onChange={(e) => setMomo(e.target.value)}
                onBlur={() => {
                  setTouched((t) => ({ ...t, momo: true }));
                  if (number) setMomo(formatMomoNumber(number));
                }}
              />
              {touched.momo && number === null ? (
                <small className="sr-field-error">Enter an MTN MoMo number, e.g. 0788 123 456</small>
              ) : (
                <small className="sr-muted">
                  The number you’ll pay from. Already on this bill? Use the same number to carry on.
                </small>
              )}
            </label>
          </div>
        </section>

        <label className={`sr-confirm ${agreed ? 'is-on' : ''}`}>
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          <span className={`sr-check ${agreed ? 'is-on' : ''}`} aria-hidden>
            {agreed ? '✓' : ''}
          </span>
          <span>I confirm my name and MTN MoMo number are correct, and that I’ll pay my share from this number.</span>
        </label>
        {error && (
          <p className="sr-field-error" role="alert">
            {error}
          </p>
        )}
      </main>

      <footer className="sr-bottom">
        <button type="submit" className={`sr-pay ${ready ? '' : 'is-disabled'}`} aria-disabled={!ready}>
          {busy ? 'Joining…' : 'Continue to items'}
        </button>
      </footer>
    </form>
  );
};

type BillProps = {
  api: SplitApi;
  token: string;
  session: Session;
  guest: Guest;
  me: Participant;
  welcome: string | null;
  /** The latest thing someone else did that concerns me. */
  notice: string | null;
  live: LiveStatus;
  onSession: (session: Session) => void;
  onRefused: (error: unknown) => boolean;
  onNotYou: () => void;
};

/** What a tap on an item asks first. */
type Prompt =
  | { kind: 'share'; item: Item; others: string[]; each: number }
  | { kind: 'units'; item: Item };

/** Picks are sent this long after the last change, so a few quick taps are one request. */
export const CLAIMS_DELAY_MS = 500;

const Bill: React.FC<BillProps> = ({
  api,
  token,
  session,
  guest,
  me,
  welcome,
  notice,
  live,
  onSession,
  onRefused,
  onNotYou,
}) => {
  const hi = firstName(me.displayName);
  const vendor = session.destination?.type === 'vendor';
  const closed = session.status === 'closed';
  const locked = picksLocked(me);
  const pickable = !closed && !locked;

  // My picks as I make them; the server's once it answers.
  const [claims, setClaims] = useState<Claim[]>(me.claims);
  const [sending, setSending] = useState(false);
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dirty = useRef(false);

  useEffect(() => {
    if (!dirty.current) setClaims(me.claims);
  }, [me.claims]);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const send = (next: Claim[]) => {
    setClaims(next);
    setMessage(null);
    dirty.current = true;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSending(true);
      try {
        const fresh = await api.setClaims(token, guest.participantId, guest.participantToken, next);
        dirty.current = false;
        onSession(fresh);
      } catch (error) {
        dirty.current = false;
        setClaims(me.claims);
        if (!onRefused(error)) setMessage(error instanceof Error ? error.message : 'Your items weren’t saved. Try again.');
      } finally {
        setSending(false);
      }
    }, CLAIMS_DELAY_MS);
  };

  const shown = useMemo(() => withClaimsOf(session, me.id, claims), [session, me.id, claims]);
  const mine = participantOf(shown, me.id) ?? me;

  const tap = (item: Item) => {
    if (closed) return;
    if (locked) {
      setMessage('Your items are set because you’ve started paying.');
      return;
    }
    if (lineUnits(item) > 1) {
      setPrompt({ kind: 'units', item });
      return;
    }
    if (unitsOf(mine, item.id) > 0) {
      send(withUnits(claims, item.id, 0));
      return;
    }
    const others = othersOn(shown, item.id, me.id);
    if (others.length > 0) {
      setPrompt({ kind: 'share', item, others, each: quote(shown, item, me.id, 1) });
      return;
    }
    send(withUnits(claims, item.id, 1));
  };

  const share = me.money?.shareMinor ?? me.total?.totalMinor ?? 0;

  return (
    <>
      <main className="sr-main">
        <section className="sr-host">
          <div className="sr-host-icon" aria-hidden>
            🧾
          </div>
          <div>
            <h1>{vendor ? 'Split the bill' : `${hostName(session)} shared a bill`}</h1>
            <p>
              {closed
                ? `Hi ${hi}, this split is closed. Your share is final.`
                : locked
                ? `Hi ${hi}, your items are set while you pay.`
                : `Hi ${hi}, tick what you had. Shared something? Tick it too and the cost is split.`}
            </p>
          </div>
        </section>

        {welcome && (
          <p className="sr-card sr-small" role="status">
            {welcome}. Your items are as you left them.
          </p>
        )}
        {notice && (
          <p className="ss-note" role="status">
            {notice}
          </p>
        )}
        {message && (
          <p className="ss-note ss-warn" role="alert">
            {message}
          </p>
        )}

        <div className="sr-card sr-payer">
          <span className="sr-payer-avatar" aria-hidden>
            {hi[0]?.toUpperCase()}
          </span>
          <div>
            <strong>{me.displayName}</strong>
          </div>
          <button type="button" className="sr-link" onClick={onNotYou}>
            Not you?
          </button>
        </div>

        <MoneyPanel
          api={api}
          token={token}
          session={session}
          guest={guest}
          me={me}
          onSession={onSession}
          onRefused={onRefused}
          onError={setMessage}
        />
        <PayForOthers
          api={api}
          token={token}
          session={session}
          guest={guest}
          me={me}
          onSession={onSession}
          onRefused={onRefused}
          onError={setMessage}
        />

        <div className="sr-label-row">
          <h2 className="sr-label">THE RECEIPT</h2>
          <span className={`sr-live is-${live}`} aria-live="polite">
            <span className="sr-live-dot" aria-hidden />
            {live === 'live' ? 'Live' : live === 'connecting' ? 'Connecting…' : 'Reconnecting…'}
          </span>
        </div>

        <Paper
          session={shown}
          me={me}
          renderItem={(item) => (
            <ItemLine
              session={shown}
              item={item}
              me={me.id}
              checked={unitsOf(mine, item.id) > 0}
              onTap={pickable ? () => tap(item) : undefined}
            />
          )}
        />

        <section>
          <h2 className="sr-label">MONEY GOES TO</h2>
          <div className="sr-card sr-dest">
            <span className="sr-dest-icon" aria-hidden>
              {vendor ? '🏪' : '👤'}
            </span>
            <div>
              <strong>{session.destination?.name || (vendor ? 'Vendor' : hostName(session))}</strong>
              <span className="sr-muted">
                {vendor
                  ? `Vendor code ${session.destination?.displayIdentifier ?? ''}`
                  : `MTN MoMo · ${session.destination?.displayIdentifier ?? ''}`}
              </span>
            </div>
          </div>
        </section>

        <GetTheApp />
      </main>

      <footer className="sr-bottom">
        <div className="sr-bottom-row" aria-live="polite">
          <span className="sr-muted">{sending || dirty.current ? 'Updating your share…' : 'Your share'}</span>
          <strong>{formatRwf(share)}</strong>
        </div>
        <PayBar
          api={api}
          token={token}
          session={session}
          guest={guest}
          me={me}
          busy={sending || dirty.current}
          onSession={onSession}
          onRefused={onRefused}
          onError={setMessage}
        />
      </footer>

      {prompt?.kind === 'share' && (
        <Dialog onClose={() => setPrompt(null)} label={`Split ${prompt.item.name}`}>
          <h2>
            Split {prompt.item.name} with {joinNames(prompt.others)}?
          </h2>
          <p className="sr-muted">
            {prompt.others.length === 1
              ? `You’ll each pay ${formatRwf(prompt.each)}.`
              : `You’ll pay ${formatRwf(prompt.each)}, and so will each of them.`}{' '}
            {joinNames(prompt.others)} {prompt.others.length === 1 ? 'is' : 'are'} told.
          </p>
          <div className="ss-actions">
            <button type="button" className="sr-ghost" onClick={() => setPrompt(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="sr-pay"
              onClick={() => {
                send(withUnits(claims, prompt.item.id, 1));
                setPrompt(null);
              }}
            >
              Split it
            </button>
          </div>
        </Dialog>
      )}
      {prompt?.kind === 'units' && (
        <UnitsDialog
          session={shown}
          item={prompt.item}
          me={me.id}
          initial={unitsOf(mine, prompt.item.id)}
          onClose={() => setPrompt(null)}
          onDone={(units) => {
            send(withUnits(claims, prompt.item.id, units));
            setPrompt(null);
          }}
        />
      )}
    </>
  );
};

/**
 * Paying: the payment is recorded as pending first (tapping again reuses it),
 * then the dialer opens. "I've paid" tells the sharer, who confirms it once
 * the money arrives.
 */
const PayBar: React.FC<{
  api: SplitApi;
  token: string;
  session: Session;
  guest: Guest;
  me: Participant;
  busy: boolean;
  onSession: (session: Session) => void;
  onRefused: (error: unknown) => boolean;
  onError: (message: string | null) => void;
}> = ({ api, token, session, guest, me, busy, onSession, onRefused, onError }) => {
  const [working, setWorking] = useState(false);
  const [copied, setCopied] = useState(false);
  const due = me.money?.balanceMinor ?? me.total?.totalMinor ?? 0;
  const pending = me.money?.pendingMinor ?? 0;
  const destination = session.destination;
  const host = hostName(session);
  const amount = me.paymentState === 'none' ? due : pending || due;
  const dial = destination ? destinationDial(destination, amount) : '';

  /** Records (or finds) my pending payment. Null when it was refused. */
  const record = async () => {
    setWorking(true);
    onError(null);
    try {
      const { payment, session: fresh } = await api.pay(token, guest.participantId, guest.participantToken);
      onSession(fresh);
      return payment;
    } catch (error) {
      if (!onRefused(error)) onError(error instanceof Error ? error.message : 'That didn’t go through. Try again.');
      return null;
    } finally {
      setWorking(false);
    }
  };

  const pay = async () => {
    const payment = await record();
    if (payment && destination) openDialer(destinationDial(destination, payment.amountMinor));
  };

  const copy = async () => {
    const payment = me.paymentState === 'none' ? await record() : null;
    if (me.paymentState === 'none' && !payment) return;
    const code = payment && destination ? destinationDial(destination, payment.amountMinor) : dial;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked; the code is on screen */
    }
  };

  const paid = async () => {
    setWorking(true);
    onError(null);
    try {
      // Tapping Pay again reuses the pending payment, which is how its id is found.
      const { payment } = await api.pay(token, guest.participantId, guest.participantToken);
      onSession(await api.reported(token, guest.participantId, guest.participantToken, payment.id));
    } catch (error) {
      if (!onRefused(error)) onError(error instanceof Error ? error.message : 'That didn’t go through. Try again.');
    } finally {
      setWorking(false);
    }
  };

  if (session.status !== 'open' || !destination) return null;

  if (me.coveredBy && me.paymentState !== 'none') {
    return (
      <p className="sr-muted sr-small sr-center" role="status">
        Covered by {firstName(me.coveredBy)} · nothing to pay
      </p>
    );
  }

  if (me.paymentState === 'paid' && due === 0) {
    return (
      <p className="sr-muted sr-small sr-center" role="status">
        ✓ Paid. Thanks, {firstName(me.displayName)}.
      </p>
    );
  }

  if (me.paymentState === 'reported') {
    return (
      <p className="sr-muted sr-small sr-center" role="status">
        Waiting for {host} to confirm your {formatRwf(pending)}. Nothing more to do.
      </p>
    );
  }

  if (me.paymentState === 'pending') {
    return (
      <div className="sr-paying">
        <p className="sr-muted sr-small sr-center">
          {isMobile()
            ? 'Your phone should open the dialer. If it doesn’t, copy the payment code and paste it in your phone app.'
            : `Dial ${dial} on your phone to pay ${formatRwf(amount)}.`}
        </p>
        <div className="sr-paying-actions">
          <button className="sr-ghost" onClick={copy} disabled={working} aria-live="polite">
            {copied ? 'Copied ✓' : 'Copy code'}
          </button>
          <button className="sr-pay" onClick={paid} disabled={working}>
            I’ve paid
          </button>
        </div>
      </div>
    );
  }

  if (due <= 0) {
    return (
      <button className="sr-pay is-disabled" disabled>
        Pick what you had
      </button>
    );
  }
  // Paid before, and the share went up since.
  const balance = me.paymentState === 'paid';
  return (
    <div className="sr-paying">
      <button className={`sr-pay ${busy || working ? 'is-disabled' : ''}`} disabled={busy || working} onClick={pay}>
        {working ? 'One moment…' : balance ? `Pay balance · ${formatRwf(due)}` : `Pay ${formatRwf(due)} with MoMo`}
      </button>
      {!isMobile() && (
        <button className="sr-back" onClick={copy} disabled={busy || working}>
          {copied ? 'Copied ✓' : 'Copy code instead'}
        </button>
      )}
    </div>
  );
};

/**
 * Where I stand after shares changed: a balance to pay, a refund coming
 * ("send to me"), money paid over my share (vendor), and paybacks between
 * guests who shared an item after it was paid for (vendor).
 */
const MoneyPanel: React.FC<{
  api: SplitApi;
  token: string;
  session: Session;
  guest: Guest;
  me: Participant;
  onSession: (session: Session) => void;
  onRefused: (error: unknown) => boolean;
  onError: (message: string | null) => void;
}> = ({ api, token, session, guest, me, onSession, onRefused, onError }) => {
  const [skipped, setSkipped] = useState<Set<string>>(() => new Set());
  const [working, setWorking] = useState<string | null>(null);
  const [code, setCode] = useState<{ to: string; dial: string } | null>(null);
  const money = me.money;
  if (!money) return null;
  const host = hostName(session);
  const itemNames = (debt: Debt) =>
    joinNames(
      debt.receiptItemIds
        .map((id) => session.receipt.items.find((i) => i.id === id)?.name)
        .filter((name): name is string => Boolean(name)),
    );

  const run = async (key: string, action: () => Promise<Session>) => {
    setWorking(key);
    onError(null);
    try {
      onSession(await action());
    } catch (error) {
      if (!onRefused(error)) onError(error instanceof Error ? error.message : 'That didn’t go through. Try again.');
    } finally {
      setWorking(null);
    }
  };

  const payBack = (debt: Debt) =>
    run(`back-${debt.toParticipantId}`, async () => {
      const result = await api.payback(token, guest.participantId, guest.participantToken, debt.toParticipantId);
      if (result.toPhoneNumber) {
        const dial = personDial(result.toPhoneNumber, result.payment.amountMinor);
        setCode({ to: firstName(debt.toName), dial });
        openDialer(dial);
      }
      return result.session;
    });

  const lines: React.ReactNode[] = [];
  const paidBefore = me.paymentState !== 'none' && money.paidMinor > 0;
  if (paidBefore && money.balanceMinor > 0) {
    lines.push(
      <p key="up">
        Your share is now {formatRwf(money.shareMinor)}. You’ve paid {formatRwf(money.paidMinor)}.
      </p>,
    );
  }
  if (money.refundDueMinor > 0) {
    lines.push(
      <p key="refund">
        {host} will send you back {formatRwf(money.refundDueMinor)}: someone shared an item you’d paid for.
      </p>,
    );
  }
  if (money.refundedMinor > 0) {
    lines.push(
      <p key="refunded">
        {host} refunded {formatRwf(money.refundedMinor)}.
      </p>,
    );
  }
  if (money.overpaidMinor > 0) {
    lines.push(
      <p key="over">You paid {formatRwf(money.overpaidMinor)} more than your share after the receipt changed.</p>,
    );
  }
  for (const debt of money.owes) {
    const to = firstName(debt.toName);
    if (debt.pendingPaybackId) {
      lines.push(
        <p key={`owes-${debt.toParticipantId}`}>
          You’re paying {to} back {formatRwf(debt.pendingPaybackMinor ?? debt.amountMinor)}. {to} confirms when it arrives.
        </p>,
      );
      continue;
    }
    if (debt.outstandingMinor <= 0 || skipped.has(debt.toParticipantId)) continue;
    lines.push(
      <div key={`owes-${debt.toParticipantId}`}>
        <p>
          You owe {to} {formatRwf(debt.outstandingMinor)} for the {itemNames(debt)} you shared. {to} paid for it
          before you joined in.
        </p>
        <div className="ss-actions">
          <button
            type="button"
            className="sr-ghost"
            onClick={() => setSkipped(new Set([...Array.from(skipped), debt.toParticipantId]))}
          >
            Settle another way
          </button>
          <button
            type="button"
            className="sr-pay"
            disabled={working !== null}
            onClick={() => payBack(debt)}
          >
            Pay {to} back
          </button>
        </div>
      </div>,
    );
  }
  for (const debt of money.owed) {
    const from = firstName(debt.fromName);
    if (debt.pendingPaybackId) {
      const paymentId = debt.pendingPaybackId;
      lines.push(
        <div key={`owed-${debt.fromParticipantId}`}>
          <p>
            {from} is paying you back {formatRwf(debt.pendingPaybackMinor ?? debt.amountMinor)} for the{' '}
            {itemNames(debt)}.
          </p>
          <button
            type="button"
            className="sr-pay"
            disabled={working !== null}
            onClick={() =>
              run(`got-${debt.fromParticipantId}`, () =>
                api.paybackReceived(token, guest.participantId, guest.participantToken, paymentId),
              )
            }
          >
            Got it
          </button>
        </div>,
      );
    } else if (debt.outstandingMinor > 0) {
      lines.push(
        <div key={`owed-${debt.fromParticipantId}`}>
          <p>
            {from} owes you {formatRwf(debt.outstandingMinor)} for sharing the {itemNames(debt)} you paid for.
          </p>
          <button
            type="button"
            className="sr-ghost"
            disabled={working !== null}
            onClick={() =>
              run(`settle-${debt.fromParticipantId}`, () =>
                api.settleDebt(token, guest.participantId, guest.participantToken, debt.fromParticipantId),
              )
            }
          >
            Mark settled
          </button>
        </div>,
      );
    }
  }
  if (code) {
    lines.push(
      <p key="code" className="sr-small sr-muted">
        To pay {code.to} back, dial {code.dial}.
      </p>,
    );
  }
  if (lines.length === 0) return null;
  return (
    <section>
      <h2 className="sr-label">YOUR MONEY</h2>
      <div className="sr-card ss-money">{lines}</div>
    </section>
  );
};

/**
 * "Pay for someone else": once I've paid, I can cover a friend who hasn't.
 * The payment is recorded on them, paid by me, and the sharer confirms it.
 */
const PayForOthers: React.FC<{
  api: SplitApi;
  token: string;
  session: Session;
  guest: Guest;
  me: Participant;
  onSession: (session: Session) => void;
  onRefused: (error: unknown) => boolean;
  onError: (message: string | null) => void;
}> = ({ api, token, session, guest, me, onSession, onRefused, onError }) => {
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<Participant | null>(null);
  const [working, setWorking] = useState(false);
  const [sent, setSent] = useState<{ name: string; dial: string } | null>(null);
  const destination = session.destination;
  const unpaid = session.participants.filter(
    (p) => p.id !== me.id && p.role !== 'host' && p.paymentState === 'none' && (p.money?.balanceMinor ?? 0) > 0,
  );
  if (session.status !== 'open' || !destination || me.paymentState === 'none' || me.coveredBy) return null;
  if (unpaid.length === 0 && !sent) return null;

  const cover = async (person: Participant) => {
    setWorking(true);
    onError(null);
    try {
      const { payment, session: fresh } = await api.pay(token, guest.participantId, guest.participantToken, person.id);
      onSession(fresh);
      const dial = destinationDial(destination, payment.amountMinor);
      setSent({ name: firstName(person.displayName), dial });
      setChosen(null);
      setOpen(false);
      openDialer(dial);
    } catch (error) {
      setChosen(null);
      if (!onRefused(error)) onError(error instanceof Error ? error.message : 'That didn’t go through. Try again.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <section>
      {sent && (
        <p className="ss-note" role="status">
          Paying for {sent.name}. If the dialer didn’t open, dial {sent.dial}. {hostName(session)} confirms it when it
          arrives.
        </p>
      )}
      {unpaid.length > 0 && !open && (
        <button type="button" className="sr-link" onClick={() => setOpen(true)}>
          Pay for someone else
        </button>
      )}
      {open && (
        <>
          <h2 className="sr-label">PAY FOR SOMEONE ELSE</h2>
          <div className="sr-card">
            {unpaid.map((p) => (
              <div key={p.id} className="sr-row">
                <span>{p.displayName}</span>
                <span className="sr-row-amount">{formatRwf(p.money?.balanceMinor ?? 0)}</span>
                <button type="button" className="sr-link" disabled={working} onClick={() => setChosen(p)}>
                  Pay
                </button>
              </div>
            ))}
          </div>
        </>
      )}
      {chosen && (
        <Dialog onClose={() => setChosen(null)} label={`Pay for ${chosen.displayName}`}>
          <h2>
            Pay {firstName(chosen.displayName)}’s {formatRwf(chosen.money?.balanceMinor ?? 0)}?
          </h2>
          <p className="sr-muted">
            Their share goes to {destination.name || (destination.type === 'vendor' ? 'the vendor' : hostName(session))}{' '}
            from your MoMo. They’ll see it’s covered by you.
          </p>
          <div className="ss-actions">
            <button type="button" className="sr-ghost" onClick={() => setChosen(null)}>
              Cancel
            </button>
            <button type="button" className="sr-pay" disabled={working} onClick={() => cover(chosen)}>
              {working ? 'One moment…' : 'Pay for them'}
            </button>
          </div>
        </Dialog>
      )}
    </section>
  );
};

/** "How many were yours?" for a line like "3× Primus". */
const UnitsDialog: React.FC<{
  session: Session;
  item: Item;
  me: string;
  initial: number;
  onClose: () => void;
  onDone: (units: number) => void;
}> = ({ session, item, me, initial, onClose, onDone }) => {
  const max = lineUnits(item);
  const [units, setUnits] = useState(initial);
  const others = ownersOf(session, item.id).filter((o) => o.participant.id !== me);
  const othersUnits = others.reduce((sum, o) => sum + o.units, 0);
  const over = units > 0 && othersUnits + units > max;
  const names = joinNames(others.map((o) => firstName(o.participant.displayName)));
  return (
    <Dialog onClose={onClose} label={`How many ${item.name} were yours?`}>
      <h2>How many were yours?</h2>
      <p className="sr-muted">
        {item.quantity}× {item.name}
        {item.unitPriceMinor != null && ` · ${formatRwf(item.unitPriceMinor)} each`}
      </p>
      {others.length > 0 && (
        <p className="sr-small">
          {others.map((o) => `${firstName(o.participant.displayName)} had ${o.units}`).join(' · ')}
        </p>
      )}
      <div className="ss-stepper">
        <button type="button" aria-label="One fewer" disabled={units <= 0} onClick={() => setUnits(units - 1)}>
          −
        </button>
        <output aria-live="polite">{units}</output>
        <button type="button" aria-label="One more" disabled={units >= max} onClick={() => setUnits(units + 1)}>
          +
        </button>
      </div>
      {over && (
        <p className="ss-note ss-warn" role="status">
          That’s more than {max}. You’ll share with {names}, each paying your part.
        </p>
      )}
      <p className="sr-muted sr-small">
        {units === 0 ? 'None of these were yours.' : `Your part: ${formatRwf(quote(session, item, me, units))}, before charges.`}
      </p>
      <div className="ss-actions">
        <button type="button" className="sr-ghost" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="sr-pay" onClick={() => onDone(units)}>
          Done
        </button>
      </div>
    </Dialog>
  );
};

/** A sheet over the page; tapping outside closes it. */
const Dialog: React.FC<{ label: string; onClose: () => void; children: React.ReactNode }> = ({
  label,
  onClose,
  children,
}) => (
  <div className="ss-backdrop" onClick={onClose}>
    <div className="ss-dialog" role="dialog" aria-modal="true" aria-label={label} onClick={(e) => e.stopPropagation()}>
      {children}
    </div>
  </div>
);

const Paper: React.FC<{ session: Session; me: Participant; children?: React.ReactNode; renderItem?: (item: Item) => React.ReactNode }> = ({
  session,
  me,
  children,
  renderItem,
}) => {
  const { receipt } = session;
  const name = receipt.merchantName?.trim() ?? '';
  const count = receipt.items.reduce((sum, i) => sum + i.quantity, 0);
  const share = me.money?.shareMinor ?? me.total?.totalMinor ?? 0;
  return (
    <article className="sr-paper">
      <div className="sr-monogram" aria-hidden>
        {name ? name[0].toUpperCase() : '🧾'}
      </div>
      <h2 className="sr-vendor">{name || 'Receipt'}</h2>
      <hr className="sr-dash" />
      <div className="sr-caps-row">
        <span>ITEM</span>
        <span>{count === 1 ? '1 ITEM' : `${count} ITEMS`}</span>
      </div>
      <ul className="sr-items">
        {receipt.items.map((item) =>
          renderItem ? (
            <React.Fragment key={item.id}>{renderItem(item)}</React.Fragment>
          ) : (
            <ItemLine key={item.id} session={session} item={item} me={me.id} />
          ),
        )}
      </ul>
      <hr className="sr-dash" />
      <div className="sr-totals">
        <Row label="Subtotal" amount={receipt.subtotalMinor} />
        {receipt.taxMinor !== 0 && <Row label="Tax" amount={receipt.taxMinor} />}
        {receipt.serviceChargeMinor !== 0 && <Row label="Service charge" amount={receipt.serviceChargeMinor} />}
        {receipt.tipMinor !== 0 && <Row label="Gratuity" amount={receipt.tipMinor} />}
        {receipt.otherChargesMinor !== 0 && <Row label="Other charges" amount={receipt.otherChargesMinor} />}
        {receipt.discountMinor !== 0 && <Row label="Discount" amount={-receipt.discountMinor} />}
        <div className="sr-grand">
          <span>TOTAL</span>
          <strong>{formatRwf(receipt.totalMinor)}</strong>
        </div>
        {(session.summary.unclaimedTotalMinor ?? 0) > 0 && (
          <Row
            label="Not picked yet"
            note={`${hostName(session)} covers what nobody picks`}
            amount={session.summary.unclaimedTotalMinor ?? 0}
          />
        )}
      </div>
      <hr className="sr-dash" />
      <div className="sr-grand sr-you-pay">
        <span>YOUR SHARE</span>
        <strong>{formatRwf(share)}</strong>
      </div>
      {children}
      <p className="sr-footer">SPLIT WITH PEBLA</p>
    </article>
  );
};

/** An item, who has it and how many. */
export const ItemLine: React.FC<{
  session: Session;
  item: Item;
  me: string;
  checked?: boolean;
  onTap?: () => void;
}> = ({ session, item, me, checked, onTap }) => {
  const owners = ownersOf(session, item.id);
  const several = item.quantity > 1;
  const mine = owners.find((o) => o.participant.id === me)?.units ?? 0;
  const others = owners.filter((o) => o.participant.id !== me);
  const caption = useMemo(() => {
    const names = others.map((o) => `${firstName(o.participant.displayName)}${several ? ` ×${o.units}` : ''}`);
    if (mine > 0 && others.length > 0) return `${several ? `You ×${mine} · ` : ''}shared with ${names.join(', ')}`;
    if (mine > 0 && several) return `You had ${mine}`;
    if (names.length > 0) return `${names.join(', ')} ${others.length === 1 ? 'has' : 'have'} this`;
    return null;
  }, [others, mine, several]);
  const itemName = item.name.trim() || 'Unnamed item';
  const content = (
    <>
      <span className="sr-qty">{item.quantity}×</span>
      <span className="sr-item-text">
        <span className="sr-item-name">{itemName}</span>
        {several && item.unitPriceMinor != null && (
          <span className="sr-small sr-muted">{formatRwf(item.unitPriceMinor)} each</span>
        )}
        {owners.length > 0 && (
          <span className="sr-avatars" aria-hidden>
            {owners.slice(0, 4).map((o) => (
              <span
                key={o.participant.id}
                className={`sr-avatar ${o.participant.id === me ? 'is-me' : ''} ${o.participant.role === 'host' ? 'is-host' : ''}`}
                title={o.participant.displayName}
              >
                {initials(o.participant.displayName) || '?'}
              </span>
            ))}
          </span>
        )}
        {caption && <span className="sr-small sr-caption">{caption}</span>}
      </span>
      <span className="sr-price">{formatRwf(item.totalPriceMinor)}</span>
      {onTap && (
        <span className={`sr-check ${checked ? 'is-on' : ''}`} aria-hidden>
          {checked ? (several ? mine : '✓') : ''}
        </span>
      )}
    </>
  );
  return (
    <li className={`sr-item ${checked ? 'is-on' : ''}`}>
      {onTap ? (
        <button
          role="checkbox"
          aria-checked={Boolean(checked)}
          aria-label={`${itemName}, ${formatRwf(item.totalPriceMinor)}${caption ? `. ${caption}` : ''}`}
          onClick={onTap}
        >
          {content}
        </button>
      ) : (
        <div>{content}</div>
      )}
    </li>
  );
};

export const Row: React.FC<{ label: string; amount: number; note?: string }> = ({ label, amount, note }) => (
  <div className="sr-row">
    <span className="sr-muted">{label}</span>
    {note && <span className="sr-small sr-muted">{note}</span>}
    <span className="sr-row-amount">{formatRwf(amount)}</span>
  </div>
);

const Notice: React.FC<{ title: string; message: string }> = ({ title, message }) => (
  <div className="sr-notice">
    <div className="sr-notice-icon" aria-hidden>
      ⏱
    </div>
    <h1>{title}</h1>
    <p className="sr-muted">{message}</p>
    <GetTheApp />
  </div>
);

const GetTheApp: React.FC = () => (
  <section className="sr-card sr-get-app">
    <strong>Split bills faster with Pebla</strong>
    <span className="sr-muted sr-small">Scan a receipt, share the link, and see who has paid.</span>
    <div className="sr-stores">
      <a href={downloadLinks.playStore} target="_blank" rel="noopener noreferrer">
        <img src="/images/download/play-store.svg" alt="Get it on Google Play" />
      </a>
      <a href={downloadLinks.appStore} target="_blank" rel="noopener noreferrer">
        <img src="/images/download/app-store.svg" alt="Download on the App Store" />
      </a>
    </div>
  </section>
);
